import { Routes } from "@angular/router";
import { AuthGuard } from "./core/guards/auth.guard";
import { RoleGuard } from "./core/guards/role.guard";
import { PdfViewerComponent } from "./shared/components/pdf-viewer/pdf-viewer.component";
import { LogoutComponent } from "./features/customer/logout/logout.component";
import { SavedCardsResolver } from "./core/guards/saved-cards.resolver";
import { PUBLIC_CATALOG_ROUTES, catalogEnabled } from "./features/public/public.routes";
import { customerWorkspaceRedirect } from "./core/guards/customer-workspace.guard";

export const routes: Routes = [
  // The marketing landing page. Same component and same content as before —
  // only its address changed, because "/" is now the provider catalog.
  {
    path: "about",
    loadComponent: () =>
      import("./components/landing/landing.component").then(
        (m) => m.LandingComponent
      ),
    pathMatch: "full",
  },
  // {
  //   path: "landing-v1",
  //   loadComponent: () =>
  //     import("./components/landing-v1/landing-v1.component").then(
  //       (m) => m.LandingV1Component
  //     ),
  // },

  // Global Auth Routes (accessible to both customer and company)
  {
    path: "login",
    loadComponent: () =>
      import("./features/customer/login-customer/login-customer.component").then(
        (m) => m.LoginCustomerComponent
      ),
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    canLoad: [AuthGuard],
    data: { roles: ["customer"] },
  },
  {
    path: "login-service-provider",
    loadComponent: () =>
      import("./features/service-provider/login-service-provider/login-service-provider.component").then(
        (m) => m.LoginServiceProviderComponent
      ),
  },
  {
    path: "otp-verification",
    loadComponent: () =>
      import(
        "./features/customer/otp-verification/otp-verification.component"
      ).then((m) => m.OtpVerificationComponent),
  },
  {
    path: "country-selector",
    loadComponent: () =>
      import(
        "./features/customer/country-selector/country-selector.component"
      ).then((m) => m.CountrySelectorComponent),
  },

  // Public registration routes
  {
    path: "public/company-registration",
    loadComponent: () =>
      import(
        "./features/service-providers/company-registeration/company-registeration.component"
      ).then((m) => m.CompanySignUpFormComponent),
  },
  {
    path: "public/influencer-registration",
    loadComponent: () =>
      import(
        "./features/service-providers/influencer-form/influencer-form.component"
      ).then((m) => m.InfluencerSignUpFormComponent),
  },

  // Global shared routes (protected by auth but accessible to all roles)
  {
    path: "pdf-view/:id",
    component: PdfViewerComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "contact-us",
    loadComponent: () =>
      import("./features/customer/contact-us/contact-us.component").then(
        (m) => m.ContactUsComponent
      ),
  },
  {
    // Tickets are account-scoped — the list is fetched with the caller's token.
    // The route carried no guard at all, so a signed-out visitor could open
    // /tickets (and /tickets/view/:id, /tickets/chat/:id) directly and sit on a
    // broken page. AuthGuard sends them to Quick Login with a returnUrl, so
    // they land back here once signed in. No RoleGuard: the roles allowed to
    // raise tickets are not expressed in canAccessRoute today, and adding one
    // would lock out everyone who can reach this from Contact Us.
    path: "tickets",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    canLoad: [AuthGuard],
    loadChildren: () =>
      import("./features/customer/tickets/tickets.module").then(
        (m) => m.TicketsModule
      ),
  },
  {
    path: "terms-conditions",
    loadComponent: () =>
      import(
        "./features/customer/terms-conditions/terms-conditions.component"
      ).then((m) => m.TermsConditionsComponent),
  },
  {
    path: "privacy-policy",
    loadComponent: () =>
      import(
        "./features/customer/privacy-policy/privacy-policy.component"
      ).then((m) => m.PrivacyPolicyComponent),
  },

  {
    path: "logout",
    component: LogoutComponent,
  },

  // =================== CHAT ROUTES (NO SIDEBAR) ===================
  {
    path: "customer/my-chats",
    loadComponent: () =>
      import("./layouts/chat-layout/chat-layout.component").then(
        (m) => m.ChatLayoutComponent
      ),
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./features/customer/my-chats/my-chats.component").then(
            (m) => m.MyChatsComponent
          )
      },
    ],
    canActivate: [AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    data: { roles: ["customer"] },
  },
  {
    path: "company/chats",
    loadComponent: () =>
      import("./layouts/chat-layout/chat-layout.component").then(
        (m) => m.ChatLayoutComponent
      ),
    children: [
      {
        path: "",
        loadComponent: () =>
          import(
            "./features/service-provider/company-chats/company-chats.component"
          ).then((m) => m.CompanyChatsComponent)
      },
    ],
    canActivate: [AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    data: { roles: ["company", "influencer",'subaccount', "financialadvisor"] },
  },

  // =================== CUSTOMER ROUTES ===================
  {
    path: "customer",
    loadComponent: () =>
      import("./layouts/customer-layout/customer-layout.component").then(
        (m) => m.CustomerLayoutComponent
      ),
    canActivate: [AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    canLoad: [AuthGuard, RoleGuard],
    data: { roles: ["customer"] },
    children: [
      {
        path: "home",
        children: [
          {
            path: "",
            loadComponent: () =>
              import("./features/customer/home/home.component").then(
                (m) => m.HomeComponent
              ),
            // Marketing tab: companies only — influencers live on their own tab
            // below. HomeComponent reads `role` from route data.
            data: { role: "company" },
          },
          {
            path: "influencers",
            loadComponent: () =>
              import("./features/customer/home/home.component").then(
                (m) => m.HomeComponent
              ),
            data: { role: "influencer" },
          },
          {
            path: 'financial-services',
            loadComponent: () => 
              import(
                "./features/customer/financial-services/financial-services.component"
              ).then(m => m.FinancialServicesComponent),
          }
          ,
          {
            path: 'financial-services/:id',
            loadComponent: () =>
              import(
                './features/customer/financial-services/financial-advisor-detail.component'
              ).then(m => m.FinancialAdvisorDetailComponent)
          },
          {
            // The same component the public catalog serves, in workspace mode.
            // It already branches on the viewer (authed /api/profile for
            // customers, /api/public for everyone else) and it is the only
            // profile page that resumes a pending chat or brochure download —
            // the thing a signed-in client most often arrives here to finish.
            // CompanyProfilePageComponent keeps the owner-editable route.
            path: 'providers/:id',
            data: { workspace: true },
            loadComponent: () =>
              import(
                './features/public/pages/public-provider-page.component'
              ).then(m => m.PublicProviderPageComponent)
          },
          {
            // Shared links carry a name slug; accept and ignore it so an old
            // or pasted /providers/:id/:slug URL still resolves here.
            path: 'providers/:id/:slug',
            data: { workspace: true },
            loadComponent: () =>
              import(
                './features/public/pages/public-provider-page.component'
              ).then(m => m.PublicProviderPageComponent)
          }
        ]
      },
      {
        path: "companies",
        loadComponent: () =>
          import("./features/customer/companies/companies.component").then(
            (m) => m.CompaniesComponent
          ),
      },
      {
        path: "companies/:companyId",
        loadComponent: () =>
          import(
            "./features/customer/company-form/company-form.component"
          ).then((m) => m.CompanyFormComponent),
      },
      {
        path: "add-company",
        loadComponent: () =>
          import(
            "./features/customer/company-form/company-form.component"
          ).then((m) => m.CompanyFormComponent),
      },
      {
        path: "invoices",
        loadComponent: () =>
          import("./features/customer/invoices/invoices.component").then(
            (m) => m.InvoicesComponent
          ),
      },
      {
        path: "invoice-details/:id",
        loadComponent: () =>
          import(
            "./features/customer/invoice-details/invoice-details.component"
          ).then((m) => m.InvoiceDetailsComponent),
      },
      {
        path: "payment-method",
        loadComponent: () =>
          import(
            "./features/customer/payment-method/payment-method.component"
          ).then((m) => m.PaymentMethodComponent),
      },
      {
        path: "payment",
        children: [
          {
            path: "select",
            loadComponent: () =>
              import(
                "./features/customer/payment-method-selection.component"
              ).then((m) => m.PaymentMethodSelectionComponent),
            resolve: { savedCards: SavedCardsResolver },
          },
          {
            path: "new",
            loadComponent: () =>
              import(
                "./features/customer/payment-page/payment-page.component"
              ).then((m) => m.PaymentPageComponent),
          },
          {
            path: "saved",
            loadComponent: () =>
              import(
                "./features/customer/payment-using-saved-method/payment-using-saved-method.component"
              ).then((m) => m.PaymentUsingSavedMethodComponent),
          },
          {
            path: "",
            redirectTo: "select",
            pathMatch: "full",
          },
        ],
      },
      {
        path: "projects",
        children: [
          {
            path: "",
            loadComponent: () =>
              import(
                "./features/customer/components/projects-list/projects-list.component"
              ).then((m) => m.ProjectsListComponent),
          },
          {
            path: "create",
            loadComponent: () =>
              import("./components/create-project/create-project.component").then(
                (m) => m.CreateProjectComponent
              ),
          },
          {
            path: ":id",
            loadComponent: () =>
              import(
                "./features/customer/components/project-details/project-details.component"
              ).then((m) => m.ProjectDetailsComponent),
          },
        ],
      },
      {
        path: "dashboard",
        redirectTo: "home",
        pathMatch: "full",
      },
      {
        path: "",
        redirectTo: "home",
        pathMatch: "full",
      },
    ],
  },
  {
    path: 'customer/campaigns',
    loadComponent: () =>
      import('./layouts/campaign-layout/campaign-layout.component').then(
        m => m.CampaignLayoutComponent
      ),
    // Customers only (the backend already rejects anyone else). Guests are sent
    // to Quick Login and brought back here afterwards.
    canActivate: [AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    data: { roles: ["customer"] },
    children: [
      {
        path: 'create',
        loadComponent: () => 
          import('./components/create-project/create-project.component').then(
            m => m.CreateProjectComponent
          ),
      },
      // Commented out: all campaigns route
      // {
      //   path: 'all',
      //   loadComponent: () => 
      //     import('./components/all-campaigns/all-campaigns.component').then(
      //       m => m.AllCampaignsComponent
      //     ),
      // },
    ]
  },

  // =================== COMPANY ROUTES ===================
  {
    path: "company",
    children: [
      // Company Authentication (Public)
      {
        path: "auth/login",
        loadComponent: () =>
          import("./features/service-provider/login-service-provider/login-service-provider.component").then(
            (m) => m.LoginServiceProviderComponent
          ),
      },
      {
        path: "auth",
        loadComponent: () =>
          import("./layouts/auth-layout/auth-layout.component").then(
            (m) => m.AuthLayoutComponent
          ),
        children: [
          {
            path: "forget-password",
            loadComponent: () =>
              import(
                "./features/service-provider/forgot-password/forgot-password.component"
              ).then((m) => m.ForgotPasswordComponent),
          },
          {
            path: "otp-verification",
            loadComponent: () =>
              import(
                "./features/service-provider/otp-verification/otp-verification.component"
              ).then((m) => m.OtpVerificationComponent),
          },
        ],
      },
      // Company Main Layout (Protected)
      {
        path: "",
        loadComponent: () =>
          import("./layouts/company-layout/company-layout.component").then(
            (m) => m.CompanyLayoutComponent
          ),
        canActivate: [AuthGuard, RoleGuard],
        canActivateChild: [AuthGuard, RoleGuard],
        canLoad: [AuthGuard, RoleGuard],
        data: { roles: ["company", "influencer", "subaccount", "financialadvisor"] },
        children: [
          {
            path: "home",
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    "./features/service-provider/company-home/company-home.component"
                  ).then((m) => m.CompanyHomeComponent),
              },
            ]
          },
          {
            path: "campaigns",
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    "./components/service-provider/campaigns/campaigns.component"
                  ).then((m) => m.CampaignsComponent),
              },
            ]
          },
          {
            path: "chats",
            loadComponent: () =>
              import("./layouts/chat-layout/chat-layout.component").then(
                (m) => m.ChatLayoutComponent
              ),
            children: [
              {
                path: "",
                loadComponent: () =>
                  import(
                    "./features/service-provider/company-chats/company-chats.component"
                  ).then((m) => m.CompanyChatsComponent),
              },
            ],
          },
          {
            path: "profile",
            // Owner-editable Public Profile — the exact same component the
            // customer read-only route (customer/home/providers/:id) loads,
            // with editable: true. One profile implementation, two modes;
            // see company-profile-page.component.ts's doc comment. No :id
            // segment — the component resolves "my own profile" via
            // AuthService when editable and no route param is present.
            loadComponent: () =>
              import(
                "./features/customer/company-profile/company-profile-page.component"
              ).then((m) => m.CompanyProfilePageComponent),
            data: { editable: true },
          },
          {
            path: "edit-pricing-details",
            loadComponent: () =>
              import(
                "./features/service-provider/edit-pricing-details/edit-pricing-details.component"
              ).then((m) => m.EditPricingDetailsComponent),
          },
          {
            path: "financial",
            loadComponent: () =>
              import("./layouts/company-layout/company-layout.component").then(
                (m) => m.CompanyLayoutComponent
              ),
          },
        ],
        // loadComponent() {
        //   return import("./layouts/service-provider-layout/service-provider-layout.component").then(
        //     (m) => m.ServiceProviderLayoutComponent
        //   );
        // },
      },

      {
        path: "my-wallet",
        loadComponent: () =>
          import("./layouts/wallet-layout/wallet-layout.component").then(
            (m) => m.WalletLayoutComponent
          ),
        children: [
          {
            path: "overview",
            loadComponent: () =>
              import(
                "./features/service-provider/my-wallet/wallet/wallet.component"
              ).then((m) => m.WalletComponent),
          },
          {
            path: "under-progress",
            loadComponent: () =>
              import(
                "./features/service-provider/my-wallet/pending/pending.component"
              ).then((m) => m.PendingComponent),
          },
          {
            path: "completed",
            loadComponent: () =>
              import(
                "./features/service-provider/my-wallet/completed/completed.component"
              ).then((m) => m.CompletedComponent),
          },
          {
            path: "",
            redirectTo: "overview",
            pathMatch: "full",
          },
        ],
      },
      {
        path: "subaccounts",
        loadComponent: () =>
          import(
            "./layouts/sub-account-layout/sub-account-layout.component"
          ).then((m) => m.SubAccountLayout),
        children: [
          {
            path: "",
            loadComponent: () =>
              import(
                "./features/service-provider/sub-account/sub-account-home/sub-account-home.component"
              ).then((m) => m.SubAccountHomeComponent),
            data: { title: "company.subaccounts.title" },
          },
        ],
      },
      {
        path: "settings",
        loadComponent: () =>
          import("./layouts/settings-layout/settings-layout.component").then(
            (m) => m.SettingsLayoutComponent
          ),
        children: [
          {
            path: "",
            loadComponent: () =>
              import(
                "./features/service-provider/settings-home/settings-home.component"
              ).then((m) => m.SettingsHomeComponent),
          },
           {
            path: "profile",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-profile/settings-profile.component"
              ).then((m) => m.SettingsProfileComponent),
            data: { title: "company.settings.pageTitles.profile" },
          },
          {
            path: "update-bank",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-bank-details/settings-bank-details.component"
              ).then((m) => m.SettingsBankDetailsComponent),
            data: { title: "company.settings.pageTitles.bankDetails" },
          },
          {
            path: "change-password",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-password/settings-password.component"
              ).then((m) => m.SettingsPasswordComponent),
            data: { title: "company.settings.pageTitles.changePassword" },
          },
          {
            path: "forgot-password",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-forgot-password/settings-forgot-password.component"
              ).then((m) => m.SettingsForgotPasswordComponent),
            data: { title: "company.settings.pageTitles.forgotPassword" },
          },
          {
            path: "update-email",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-email-phone/settings-email-phone.component"
              ).then((m) => m.SettingsEmailPhoneComponent),
            data: { title: "company.settings.pageTitles.emailPhone" },
          },
           {
            path: "services",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-services/settings-services.component"
              ).then((m) => m.SettingsServicesComponent),
            data: { title: "company.settings.pageTitles.services" },
          },
          {
            // Freelancers' entry to the same screen. The component picks the
            // vocabulary and endpoints from the signed-in role; this route
            // exists only so the page title reads "Scope of Work".
            path: "scope-of-work",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-services/settings-services.component"
              ).then((m) => m.SettingsServicesComponent),
            data: { title: "company.settings.pageTitles.scopeOfWork" },
          },
          {
            path: "update-price",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-price-tag/settings-price-tag.component"
              ).then((m) => m.SettingsPriceTagComponent),
            data: { title: "company.settings.pageTitles.priceTag" },
          },
          {
            path: "edit-vat",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-vat/settings-vat.component"
              ).then((m) => m.SettingsVatComponent),
            data: { title: "company.settings.pageTitles.vat" },
          },
          {
            path: "loan-requirements",
            loadComponent: () =>
              import(
                "./features/financial-advisor/loan-requirements/loan-requirements.component"
              ).then((m) => m.LoanRequirementsComponent),
            data: { title: "company.settings.pageTitles.loanRequirements" },
          },
          {
            path: "delete-account",
            loadComponent: () =>
              import(
                "./features/service-provider/settings/settings-delete/settings-delete.component"
              ).then((m) => m.SettingsDeleteComponent),
          },
        ],
      },
      {
        path: "invoices",
        loadComponent: () =>
          import("./features/customer/invoices/invoices.component").then(
            (m) => m.InvoicesComponent
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "invoice-details/:id",
        loadComponent: () =>
          import(
            "./features/customer/invoice-details/invoice-details.component"
          ).then((m) => m.InvoiceDetailsComponent),
        canActivate: [AuthGuard],
      },
      {
        path: "tax-invoices",
        loadComponent: () =>
          import(
            "./features/service-provider/tax-invoices/tax-invoices.component"
          ).then((m) => m.TaxInvoicesComponent),
        canActivate: [AuthGuard],
      },
      {
        path: "tax-invoice-details/:id",
        loadComponent: () =>
          import(
            "./features/service-provider/tax-invoice-details/tax-invoice-details.component"
          ).then((m) => m.TaxInvoiceDetailsComponent),
      },
    ],
  },

  // =================== FINANCIAL ADVISOR ROUTES ===================
  {
    path: "financial-advisor",
    loadComponent: () =>
      import(
        "./layouts/financial-advisor-layout/financial-advisor-layout.component"
      ).then((m) => m.FinancialAdvisorLayoutComponent),
    canActivate: [AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    canLoad: [AuthGuard, RoleGuard],
    data: { roles: ["financialadvisor"] },
    children: [
      {
        path: "home",
        loadComponent: () =>
          import(
            "./features/financial-advisor/home/home.component"
          ).then((m) => m.FinancialAdvisorHomeComponent),
      },
      {
        path: "chats",
        loadComponent: () =>
          import("./layouts/chat-layout/chat-layout.component").then(
            (m) => m.ChatLayoutComponent
          ),
        children: [
          {
            path: "",
            loadComponent: () =>
              import(
                "./features/service-provider/company-chats/company-chats.component"
              ).then((m) => m.CompanyChatsComponent),
          },
        ],
      },
      {
        path: "loan-requirements",
        loadComponent: () =>
          import(
            "./features/financial-advisor/loan-requirements/loan-requirements.component"
          ).then((m) => m.LoanRequirementsComponent),
      },
      { path: "", redirectTo: "home", pathMatch: "full" },
    ],
  },

  // =================== PUBLIC CATALOG (guest browsing) ===================
  // The catalog is the site's front door: "/" lists every eligible provider in
  // English, "/ar" the same in Arabic. No sign-in needed anywhere in here.
  //
  // Order matters. The English catalog is unprefixed, so its route would match
  // almost anything; it is declared last, after every application route above,
  // and picks up only what is left. If none of its children match, Angular
  // backtracks and the wildcard below renders "page not found".
  {
    path: "ar",
    canMatch: [catalogEnabled],
    canActivateChild: [customerWorkspaceRedirect],
    data: { lang: "ar" },
    loadComponent: () =>
      import("./layouts/public-layout/public-layout.component").then(
        (m) => m.PublicLayoutComponent
      ),
    children: PUBLIC_CATALOG_ROUTES,
  },
  {
    path: "",
    canMatch: [catalogEnabled],
    canActivateChild: [customerWorkspaceRedirect],
    data: { lang: "en" },
    loadComponent: () =>
      import("./layouts/public-layout/public-layout.component").then(
        (m) => m.PublicLayoutComponent
      ),
    children: PUBLIC_CATALOG_ROUTES,
  },

  // Unknown URLs get a real "page not found" instead of a silent redirect.
  {
    path: "**",
    loadComponent: () =>
      import("./features/public/pages/not-found-page.component").then(
        (m) => m.NotFoundPageComponent
      ),
  },
];
